import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useAnularGasto } from './api';

interface Props {
  condoId: string;
  gastoId: string;
}

export function AnularDialog({ condoId, gastoId }: Props) {
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const anular = useAnularGasto(condoId);

  const motivoValido = motivo.trim().length >= 10;

  async function handleAnular() {
    if (!motivoValido) return;
    try {
      await anular.mutateAsync({ gastoId, motivo: motivo.trim() });
      toast.success('Gasto comun anulado');
      setOpen(false);
      setMotivo('');
    } catch {
      toast.error('No se pudo anular el gasto comun');
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="danger" size="sm">
          Anular
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anular gasto comun</DialogTitle>
          <DialogDescription>
            Esta accion anulara el gasto comun y todos sus cargos pendientes. Los cargos
            ya pagados se marcaran como reembolsados. Esta accion no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="motivo">Motivo de anulacion</Label>
          <textarea
            id="motivo"
            className="flex min-h-[100px] w-full rounded-md border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm placeholder:text-[var(--color-ink-faint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Describe el motivo de la anulacion (minimo 10 caracteres)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          {motivo.length > 0 && !motivoValido && (
            <p className="text-xs text-[var(--color-danger)]">
              El motivo debe tener al menos 10 caracteres
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleAnular}
            disabled={!motivoValido || anular.isPending}
          >
            {anular.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Anulando...
              </>
            ) : (
              'Confirmar anulacion'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
